import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PageHeaderAddButton,
	PageHeaderListActions,
	Pagination,
	PaginationHeader,
	PopupSnackbar,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

import StaffListing from "./staff-listing";
import StaffAddEditForm from "./staff-add-edit-container";
import {
	isConsultantUser,
	getPageResults,
	useIsBornAccount,
	canManageStaff,
	getVisibleStaffDirectoryIds,
	isInStaffDirectory,
} from "utils";
import { INFO_HOME_ROUTE } from "features/home/home-routes";
import BatchCreateStaffForm from "./staff-batch-create-form";

const useStyles = makeStyles((theme) => ({
	root: {},
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	header: {
		margin: theme.spacing(0, 0, 2, 0),
	},
	divider: {
		marginTop: theme.spacing(1),
		height: 1,
	},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const StaffList = ({
	profile,
	organisations,
	onEdit,
	onDelete,
	onImpersonate,
	onAdd,
	onSendResetEmail,
	onSendConfirmationEmail,
	onGetStaffList,
	pagination,
	directory,
	apiError,
	onListLoaded,
}) => {
	const classes = useStyles();
	const isBornAccount = useIsBornAccount();
	const [showError, setShowError] = useState(false);
	const [showImpersonationLoading, setImpersonationLoading] = useState(false);
	const [showBatchCreateModal, setShowBatchCreateModal] = useState(false);
	const allStaff = [...getPageResults(pagination)].sort((a, b) => {
		const aActive = String(a.status).toLowerCase() === "active";
		const bActive = String(b.status).toLowerCase() === "active";
		return (bActive ? 1 : 0) - (aActive ? 1 : 0);
	});
	const directoryIds = getVisibleStaffDirectoryIds(
		profile.role,
		profile.supervisingStaffList
	);
	const staffList =
		directoryIds == null
			? allStaff
			: allStaff.filter((member) =>
					isInStaffDirectory(directoryIds, member.id)
			  );
	const displayPagination =
		directoryIds == null
			? pagination
			: {
					...pagination,
					results: staffList,
					totalNumberOfRecords: staffList.length,
			  };
	const mayManageStaff = canManageStaff(profile.role);
	const currentStaffId = profile.staffId;
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);

	useEffect(() => {
		onListLoaded();
		setShowError(false);
	}, [onListLoaded, setShowError]);

	useEffect(() => {
		setShowError(apiError && apiError !== "");
		setImpersonationLoading(false);
	}, [apiError, setShowError]);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Only trigger search if string is empty (reset) or 3+ characters
			if (searchFilter === "" || searchFilter.length >= 3) {
				onGetStaffList({
					page: 1,
					pageSize: 5000,
					orderBy: "status,createdAt",
					ascending: true,
					searchKey: searchFilter
				});
			}
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter, onGetStaffList]);

	const isCreator = (staff) => {
		const organisation = organisations.filter(
			(node) => node.creatorId === staff.id
		);
		return !!organisation.length;
	};

	return (
		<Page className={classes.root} title="Staff">
			<ScrollToTopOnMount />
							<PageHeaderAddButton
					title="Staff"
					addTabTitle="New"
					onAdd={onAdd}
					showAddButton={mayManageStaff}
					backActionPath={INFO_HOME_ROUTE}
					secondaryTabTitle="Batch Create Staff"
					onSecondaryAction={
						mayManageStaff && isBornAccount
							? () => setShowBatchCreateModal(true)
							: null
					}
					titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}
				>
					{/* START: Search */}
					{showFilters && (
						<div className={classes.gridContainer}>
							<Grid container spacing={2} className={classes.filterGrid}>
								<Grid item xs={12} md={6}>
									<TextField
										className={classes.search}
										id="input-with-icon-textfield"
										variant="outlined"
										label="Search"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
											endAdornment: searchFilter && (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
														onClick={() => {
															setSearchFilter("");
															onGetStaffList({
																page: 1,
																pageSize: 5000,
																orderBy: "status,createdAt",
																ascending: true,
																searchKey: ""
															});
														}}
														edge="end"
														size="small"
													>
														<ClearIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
										value={searchFilter}
										onChange={(val) => {
											const { value } = val.target;
											setSearchFilter(value);
											// Real-time search with debounce effect will handle the API call
										}}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
										placeholder={"Search by staff name or email"}
									/>
								</Grid>
							</Grid>
						</div>
					)}
					{/* END: Search */}
				</PageHeaderAddButton>

			<PageContent pad>
				<div className={classes.body}>
					<PaginationHeader hidePagination pagination={displayPagination} />
					{staffList.map((staff, index) => (
						<StaffListing
							editable={
								isCreator(staff) ||
								isConsultantUser(staff.organisationRole)
									? staff.id === currentStaffId
									: true
							}
							key={index}
							staff={staff}
							staffList={directory && directory.length ? directory : allStaff}
							visibleStaffIds={directoryIds}
							organisations={organisations}
							onEdit={onEdit}
							onDelete={onDelete}
							onImpersonate={(id) => {
								onImpersonate(id);
								setImpersonationLoading(true);
							}}
							onSendResetEmail={onSendResetEmail}
							onSendConfirmationEmail={onSendConfirmationEmail}
							profile={profile}
						/>
					))}
					{showImpersonationLoading && (
						<PopupSnackbar
							message={
								"Requesting impersonation...please do not refresh the page."
							}
							coloured
						/>
					)}

					{showError && (
						<PopupSnackbar message={apiError} coloured error />
					)}
				</div>

				<Pagination
					hidePagination
					onGetList={onGetStaffList}
					pagination={displayPagination}
					orderBy="status,createdAt"
					searchKey={searchFilter}
				/>
			</PageContent>

			<StaffAddEditForm />

			{showBatchCreateModal && (
				<BatchCreateStaffForm
					open={true}
					onClose={() => setShowBatchCreateModal(false)}
				/>
			)}
		</Page>
	);
};

StaffList.propTypes = {
	profile: PropTypes.object.isRequired,
	organisations: PropTypes.array.isRequired,
	onEdit: PropTypes.func.isRequired,
	onAdd: PropTypes.func,
	onSendResetEmail: PropTypes.func.isRequired,
	onSendConfirmationEmail: PropTypes.func.isRequired,
	onGetStaffList: PropTypes.func.isRequired,
	pagination: PropTypes.object.isRequired,
	directory: PropTypes.array,
	apiError: PropTypes.string,
	onListLoaded: PropTypes.func.isRequired,
};

export default StaffList;
